
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

interface GeneralSystemSettingsProps {
  editing: boolean;
}

const GeneralSystemSettings: React.FC<GeneralSystemSettingsProps> = ({ editing }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">General System Settings</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="system-timezone">System Timezone</Label>
            <Select disabled={!editing} defaultValue="UTC">
              <SelectTrigger id="system-timezone">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UTC">UTC</SelectItem>
                <SelectItem value="EST">Eastern Standard Time</SelectItem>
                <SelectItem value="CST">Central Standard Time</SelectItem>
                <SelectItem value="PST">Pacific Standard Time</SelectItem>
                <SelectItem value="IST">India Standard Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="data-refresh">Data Refresh Interval (seconds)</Label>
            <Input id="data-refresh" type="number" defaultValue="60" readOnly={!editing} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="system-language">System Language</Label>
            <Select disabled={!editing} defaultValue="en">
              <SelectTrigger id="system-language">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Spanish</SelectItem>
                <SelectItem value="fr">French</SelectItem>
                <SelectItem value="de">German</SelectItem>
                <SelectItem value="ja">Japanese</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="data-retention">Data Retention Period (days)</Label>
            <Input id="data-retention" type="number" defaultValue="90" readOnly={!editing} />
          </div>
        </div>
        
        <div className="space-y-4 pt-2">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="debug-mode" className="text-base">Debug Mode</Label>
              <p className="text-sm text-muted-foreground">Enable detailed logging for troubleshooting</p>
            </div>
            <Switch id="debug-mode" disabled={!editing} />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="maintenance-mode" className="text-base">Maintenance Mode</Label>
              <p className="text-sm text-muted-foreground">Temporarily disable user access for system maintenance</p>
            </div>
            <Switch id="maintenance-mode" disabled={!editing} />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-backup" className="text-base">Automatic Backups</Label>
              <p className="text-sm text-muted-foreground">Schedule regular system configuration backups</p>
            </div>
            <Switch id="auto-backup" defaultChecked disabled={!editing} />
          </div>
        </div>
        
        <div className="space-y-2 pt-2">
          <Label htmlFor="system-notes">System Notes</Label>
          <Textarea 
            id="system-notes" 
            placeholder="Add system-wide notes visible to administrators" 
            className="min-h-[100px]"
            readOnly={!editing}
            defaultValue="System upgrade scheduled for next maintenance window. Contact IT for details."
          />
        </div>
      </div>
    </div>
  );
};

export default GeneralSystemSettings;
